// Hero.jsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Hero.css";

function Hero() {
  const heroRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const el = heroRef.current;
    // Trigger on mount. If you use an IntersectionObserver, replace this.
    requestAnimationFrame(() => el?.classList.add("is-visible"));
  }, []);

  const [frontIsA, setFrontIsA] = useState(true);
  const layerARef = useRef(null);
  const layerBRef = useRef(null);
  const rafId = useRef(0);

  // Check if we're on the home page
  const isHomePage = location.pathname === "/";

  // Function to handle navigation to about section
  const handleAboutNavigation = (e) => {
    e.preventDefault();

    if (isHomePage) {
      // If already on home page, just scroll to about section
      const element = document.getElementById("about");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // If on different page, navigate to home first, then scroll
      navigate("/");
      // Wait a bit for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById("about");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  const setImage = (url) => {
    const front = frontIsA ? layerARef.current : layerBRef.current;
    const back = frontIsA ? layerBRef.current : layerARef.current;
    if (!front || !back) return;
    back.style.backgroundImage = `url('${url}')`;
    back.classList.add("show");
    front.classList.remove("show");
    setFrontIsA(!frontIsA);
  };

  const clearImage = () => {
    const front = frontIsA ? layerARef.current : layerBRef.current;
    front?.classList.remove("show");
  };

  const onMouseMove = (e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    const mx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
    const my = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      layerARef.current?.style.setProperty("--mx", mx.toFixed(3));
      layerARef.current?.style.setProperty("--my", my.toFixed(3));
      layerBRef.current?.style.setProperty("--mx", mx.toFixed(3));
      layerBRef.current?.style.setProperty("--my", my.toFixed(3));
    });
  };

  return (
    <section
      className="hero"
      ref={heroRef}
      onMouseMove={onMouseMove}
      onMouseLeave={clearImage}
    >
      <h2 className="hero-title">
        <span className="line">
          <span>
            Hello{" "}
            <span className="hero-hand" role="img" aria-label="Waving Hand">
              👋
            </span>{" "}
            I'm{" "}
            <a
              href="#about"
              onClick={handleAboutNavigation}
              onMouseEnter={() =>
                setImage(
                  "https://images.mupende.com/assets/IMG_1707_preview.jpeg"
                )
              }
              onMouseLeave={clearImage}
            >
              Charles
            </a>
            , a product engineer
          </span>
        </span>
        <span className="line">
          <span>
            based in{" "}
            <a
              href="#stuttgart"
              onMouseEnter={() =>
                setImage(
                  "https://plus.unsplash.com/premium_photo-1716932567535-6bb42a3f38ff?q=80&w=2832&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                )
              }
              onMouseLeave={clearImage}
            >
              Stuttgart
            </a>
            , currently designing and
          </span>
        </span>
        <span className="line">
          <span>
            building software at{" "}
            <a
              href="https://www.datacook.de"
              style={{
                fontFamily: "Helvetica Neue, sans-serif",
                fontWeight: "bold",
              }}
              onMouseEnter={() =>
                setImage(
                  "https://images.unsplash.com/photo-1542903660-eedba2cda473?q=80&w=2340&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                )
              }
              onMouseLeave={clearImage}
            >
              <span style={{ color: "#2d85e2" }}>[</span>d
              <span style={{ color: "#2d85e2" }}>]</span>atacook
            </a>
            .
          </span>
        </span>
      </h2>

      <p className="hero-subtitle">
        <span className="line">
          <span>
            I’m a product engineer with several years of experience in software
            design, focused on machine learning and intelligent systems to build
            tools that deliver real value to users.
          </span>
        </span>
      </p>
      <div className="hero-bg-stack" aria-hidden>
        <div ref={layerARef} className="hero-bg-layer" />
        <div ref={layerBRef} className="hero-bg-layer" />
      </div>
    </section>
  );
}

export default Hero;
